<template>
  <v-container fluid class="pa-6 pa-md-8">
    <div class="page-head d-flex flex-wrap align-center justify-space-between">
      <div>
        <h1 class="page-head__title">Transaction Statistics</h1>
        <p class="page-head__sub">
          Transaction volume and value trends over time.
        </p>
      </div>
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="dates"
        transition="scale-transition"
        offset-y
        min-width="auto"
        :nudge-left="120"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            outlined
            v-bind="attrs"
            v-on="on"
            class="toolbar-btn text-none"
            :class="{ 'toolbar-btn--active': dates.length > 0 }"
          >
            <v-icon small left>mdi-calendar-range-outline</v-icon>
            {{ dates.length > 0 ? dateRangeText : "Choose date range" }}
            <v-icon
              v-if="dates.length === 2"
              small
              right
              @click.stop="clearDateFilter"
              >mdi-close</v-icon
            >
          </v-btn>
        </template>
        <v-date-picker v-model="dates" range :max="today" color="primary">
          <v-spacer></v-spacer>
          <v-btn small text color="primary" @click="menu = false">Cancel</v-btn>
          <v-btn @click="setFilteredDate" text color="primary">OK</v-btn>
        </v-date-picker>
      </v-menu>
    </div>

    <!-- Summary tiles -->
    <v-row class="mb-1">
      <v-col cols="12" sm="4">
        <div class="stat-tile">
          <div class="stat-tile__icon stat-tile__icon--brand">
            <v-icon size="20">mdi-chart-line</v-icon>
          </div>
          <div>
            <p class="stat-tile__label">Total Transactions</p>
            <p class="stat-tile__value tabular">{{ totalTransactions }}</p>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-tile">
          <div class="stat-tile__icon stat-tile__icon--success">
            <v-icon size="20">mdi-cash-multiple</v-icon>
          </div>
          <div>
            <p class="stat-tile__label">Total Amount</p>
            <p class="stat-tile__value tabular">
              {{ totalAmount | currencyFormat }}
            </p>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-tile">
          <div class="stat-tile__icon stat-tile__icon--info">
            <v-icon size="20">mdi-calendar-range</v-icon>
          </div>
          <div>
            <p class="stat-tile__label">Date Range</p>
            <p class="stat-tile__value stat-tile__value--sm">
              {{ displayDateRange }}
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Charts -->
    <v-row>
      <v-col cols="12" lg="6">
        <div class="panel">
          <div class="panel__head">
            <v-icon small class="mr-2">mdi-chart-timeline-variant</v-icon>
            Transaction Count by Date
          </div>
          <div class="panel__body">
            <apexchart
              v-if="!pageLoading && chartSeries.length > 0"
              type="line"
              height="330"
              :options="lineChartOptions"
              :series="transactionCountSeries"
            ></apexchart>
            <div v-else-if="pageLoading" class="panel__empty">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else class="panel__empty text-muted">No data available</div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" lg="6">
        <div class="panel">
          <div class="panel__head">
            <v-icon small class="mr-2">mdi-trending-up</v-icon>
            Transaction Amount by Date
          </div>
          <div class="panel__body">
            <apexchart
              v-if="!pageLoading && chartSeries.length > 0"
              type="area"
              height="330"
              :options="areaChartOptions"
              :series="transactionAmountSeries"
            ></apexchart>
            <div v-else-if="pageLoading" class="panel__empty">
              <v-progress-circular indeterminate color="success" />
            </div>
            <div v-else class="panel__empty text-muted">No data available</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Table -->
    <div class="mt-2">
      <div class="d-flex align-center mb-3">
        <h2 class="section-title mb-0">Detailed statistics</h2>
        <v-spacer />
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <v-btn
              :loading="pageLoading"
              @click="refreshData"
              v-on="on"
              icon
              class="toolbar__refresh"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh statistics</span>
        </v-tooltip>
      </div>
      <v-data-table
        :headers="headers"
        :items="statisticsData"
        :loading="pageLoading"
        loading-text="Loading statistics… Please wait"
        class="data-table"
        :options.sync="tableOptions"
        :server-items-length="transactionStatisticsPagination.totalRecords"
        :footer-props="{ itemsPerPageOptions: [10, 20, 30, 50] }"
      >
        <template #item.date="{ item }">
          <span class="cell-strong">{{ item.date | dateFormat }}</span>
        </template>
        <template #item.totalTransactions="{ item }">
          <span class="tabular">{{ item.totalTransactions }}</span>
        </template>
        <template #item.totalAmount="{ item }">
          <span class="tabular cell-amount">{{
            item.totalAmount | currencyFormat
          }}</span>
        </template>
        <template #item.averageAmount="{ item }">
          <span class="tabular">{{ item.averageAmount | currencyFormat }}</span>
        </template>
      </v-data-table>
    </div>

    <!-- Analytics breakdown -->
    <div class="mt-6">
      <h2 class="section-title">Transaction breakdown</h2>
      <v-row>
        <v-col cols="12" lg="8">
          <div class="panel">
            <div class="panel__head">
              <v-icon small class="mr-2">mdi-clock-outline</v-icon>
              Activity by hour of day
            </div>
            <div class="panel__body">
              <apexchart
                v-if="hasAnalytics"
                type="area"
                height="300"
                :options="hourOptions"
                :series="hourSeries"
              ></apexchart>
              <div v-else class="panel__empty text-muted">No data available</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" lg="4">
          <div class="panel">
            <div class="panel__head">
              <v-icon small class="mr-2">mdi-chart-donut</v-icon>
              By status
            </div>
            <div class="panel__body">
              <apexchart
                v-if="hasAnalytics"
                type="donut"
                height="300"
                :options="statusOptions"
                :series="statusSeries"
              ></apexchart>
              <div v-else class="panel__empty text-muted">No data available</div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" lg="6">
          <div class="panel">
            <div class="panel__head">
              <v-icon small class="mr-2">mdi-format-list-bulleted</v-icon>
              By service
            </div>
            <div class="panel__body">
              <apexchart
                v-if="hasAnalytics"
                type="bar"
                height="320"
                :options="serviceOptions"
                :series="serviceSeries"
              ></apexchart>
              <div v-else class="panel__empty text-muted">No data available</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" lg="6">
          <div class="panel">
            <div class="panel__head">
              <v-icon small class="mr-2">mdi-cash-100</v-icon>
              By amount range
            </div>
            <div class="panel__body">
              <apexchart
                v-if="hasAnalytics"
                type="bar"
                height="320"
                :options="bucketOptions"
                :series="bucketSeries"
              ></apexchart>
              <div v-else class="panel__empty text-muted">No data available</div>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import SnackBarMixins from "@/mixins/SnackBarMIxins";

export default {
  name: "TransactionStatistics",
  mixins: [SnackBarMixins],
  data() {
    return {
      menu: false,
      dates: [],
      today: moment().format("YYYY-MM-DD"),
      tableOptions: {
        page: 1,
        itemsPerPage: 10
      }
    };
  },
  computed: {
    ...mapGetters({
      pageLoading: "getIsPageLoading",
      statistics: "reports/getTransactionStatistics",
      analytics: "reports/getTransactionAnalytics",
      transactionStatisticsPagination:
        "reports/getTransactionStatisticsPagination"
    }),
    hasAnalytics() {
      return !!(
        this.analytics &&
        Array.isArray(this.analytics.byHour) &&
        this.analytics.byHour.length
      );
    },
    hourData() {
      return [...((this.analytics && this.analytics.byHour) || [])].sort(
        (a, b) => a.hour - b.hour
      );
    },
    hourSeries() {
      return [{ name: "Transactions", data: this.hourData.map(h => h.count) }];
    },
    hourOptions() {
      return {
        chart: {
          type: "area",
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        theme: { mode: this.isDark ? "dark" : "light" },
        colors: ["#ff0055"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: { opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 95, 100] }
        },
        xaxis: {
          categories: this.hourData.map(h => String(h.hour).padStart(2, "0")),
          title: { text: "Hour of day" },
          tickAmount: 12,
          axisTicks: { show: false }
        },
        yaxis: { labels: { formatter: v => Math.round(v) } },
        tooltip: {
          theme: this.isDark ? "dark" : "light",
          x: { formatter: v => `${v}:00` },
          y: { formatter: v => `${v} transactions` }
        },
        grid: { borderColor: this.chartTheme.grid, strokeDashArray: 4 }
      };
    },
    statusData() {
      return (this.analytics && this.analytics.byStatus) || [];
    },
    statusSeries() {
      return this.statusData.map(s => s.count);
    },
    statusOptions() {
      const colorMap = {
        COMPLETED: "#0f9d63",
        FAILED: "#e11d48",
        STARTED: "#2f6df0"
      };
      return {
        chart: { type: "donut", background: "transparent" },
        theme: { mode: this.isDark ? "dark" : "light" },
        labels: this.statusData.map(s => s.status),
        colors: this.statusData.map(s => colorMap[s.status] || "#6b7280"),
        stroke: { colors: ["transparent"] },
        legend: {
          position: "bottom",
          labels: { colors: this.chartTheme.foreColor }
        },
        dataLabels: { formatter: v => `${Math.round(v)}%` },
        plotOptions: { pie: { donut: { size: "68%" } } },
        tooltip: { theme: this.isDark ? "dark" : "light" }
      };
    },
    serviceData() {
      return [...((this.analytics && this.analytics.byService) || [])].sort(
        (a, b) => b.count - a.count
      );
    },
    serviceSeries() {
      return [
        { name: "Transactions", data: this.serviceData.map(s => s.count) }
      ];
    },
    serviceOptions() {
      return {
        chart: {
          type: "bar",
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: { show: false }
        },
        theme: { mode: this.isDark ? "dark" : "light" },
        colors: ["#ff0055"],
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4, barHeight: "62%" }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.serviceData.map(s => s.service),
          title: { text: "Transactions" }
        },
        grid: { borderColor: this.chartTheme.grid, strokeDashArray: 4 },
        tooltip: { theme: this.isDark ? "dark" : "light" }
      };
    },
    bucketData() {
      return (this.analytics && this.analytics.byAmountBucket) || [];
    },
    bucketSeries() {
      return [
        { name: "Transactions", data: this.bucketData.map(b => b.count) }
      ];
    },
    bucketOptions() {
      return {
        chart: {
          type: "bar",
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: { show: false }
        },
        theme: { mode: this.isDark ? "dark" : "light" },
        colors: ["#2f6df0"],
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.bucketData.map(b => this.bucketLabel(b._id)),
          title: { text: "Amount (GHS)" }
        },
        yaxis: { labels: { formatter: v => Math.round(v) } },
        grid: { borderColor: this.chartTheme.grid, strokeDashArray: 4 },
        tooltip: { theme: this.isDark ? "dark" : "light" }
      };
    },
    isDark() {
      return this.$vuetify.theme.dark;
    },
    chartTheme() {
      return {
        foreColor: this.isDark ? "#9aa3ae" : "#545d68",
        grid: this.isDark ? "rgba(255,255,255,0.08)" : "#e4e7eb"
      };
    },
    dateRangeText() {
      if (this.dates.length === 2) {
        const sortedDates = [...this.dates].sort();
        const [start, end] = sortedDates;
        return `${moment(start).format("MMM DD")} ~ ${moment(end).format(
          "MMM DD, YYYY"
        )}`;
      }
      return this.dates.join(" ~ ");
    },
    displayDateRange() {
      if (this.dates.length === 2) {
        const sortedDates = [...this.dates].sort();
        const [start, end] = sortedDates;
        return `${moment(start).format("MMM DD")} - ${moment(end).format(
          "MMM DD, YYYY"
        )}`;
      }
      return "All Time";
    },
    statisticsData() {
      return this.statistics.map(item => ({
        ...item,
        averageAmount:
          item.totalTransactions > 0
            ? item.totalAmount / item.totalTransactions
            : 0
      }));
    },
    chartSeries() {
      return this.statistics.map(item => item.date);
    },
    transactionCountSeries() {
      return [
        {
          name: "Transaction Count",
          data: this.statistics.map(item => item.totalTransactions)
        }
      ];
    },
    transactionAmountSeries() {
      return [
        {
          name: "Total Amount (GHS)",
          data: this.statistics.map(item => item.totalAmount)
        }
      ];
    },
    totalTransactions() {
      return this.statistics.reduce(
        (sum, item) => sum + item.totalTransactions,
        0
      );
    },
    totalAmount() {
      return this.statistics.reduce((sum, item) => sum + item.totalAmount, 0);
    },
    headers() {
      return [
        {
          text: "Date",
          value: "date",
          filterable: false,
          sortable: true
        },
        {
          text: "Total Transactions",
          value: "totalTransactions",
          filterable: false,
          sortable: true
        },
        {
          text: "Total Amount (GHS)",
          value: "totalAmount",
          filterable: false,
          sortable: true
        },
        {
          text: "Average Amount (GHS)",
          value: "averageAmount",
          filterable: false,
          sortable: true
        }
      ];
    },
    lineChartOptions() {
      return {
        chart: {
          type: "line",
          height: 330,
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          zoom: { enabled: true }
        },
        theme: { mode: this.isDark ? "dark" : "light" },
        colors: ["#ff0055"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        xaxis: {
          categories: this.chartSeries,
          title: { text: "Date" },
          labels: {
            formatter: value => moment(value).format("MMM DD")
          }
        },
        yaxis: {
          title: { text: "Number of Transactions" }
        },
        tooltip: {
          theme: this.isDark ? "dark" : "light",
          x: {
            formatter: (value, opts) => this.tooltipDate(opts)
          },
          y: {
            formatter: value => `${value} transactions`
          }
        },
        grid: { borderColor: this.chartTheme.grid }
      };
    },
    areaChartOptions() {
      return {
        chart: {
          type: "area",
          height: 330,
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          zoom: { enabled: true }
        },
        theme: { mode: this.isDark ? "dark" : "light" },
        colors: ["#0f9d63"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.5,
            opacityTo: 0.1,
            stops: [0, 90, 100]
          }
        },
        xaxis: {
          categories: this.chartSeries,
          title: { text: "Date" },
          labels: {
            formatter: value => moment(value).format("MMM DD")
          }
        },
        yaxis: {
          title: { text: "Amount (GHS)" },
          labels: {
            formatter: value => `GHS ${this.compactAmount(value)}`
          }
        },
        tooltip: {
          theme: this.isDark ? "dark" : "light",
          x: {
            formatter: (value, opts) => this.tooltipDate(opts)
          },
          y: {
            formatter: value => this.money(value)
          }
        },
        grid: { borderColor: this.chartTheme.grid }
      };
    }
  },
  filters: {
    dateFormat(value) {
      return moment(value).format("MMM DD, YYYY");
    },
    currencyFormat(value) {
      return `GHS ${Number(value || 0).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  },
  watch: {
    dates: {
      handler(newDates, oldDates) {
        // Only validate when we have exactly 2 dates and the array changed
        if (
          newDates.length === 2 &&
          JSON.stringify(newDates) !== JSON.stringify(oldDates)
        ) {
          const sortedDates = [...newDates].sort(); // Create a copy before sorting
          const [start, end] = sortedDates;
          if (moment(start).isAfter(end)) {
            this.$store.dispatch(
              "showSnackBar",
              {
                snackBar: true,
                message: "Start date cannot be after end date",
                status: "warning"
              },
              { root: true }
            );
          }
        }
      },
      deep: true
    },
    tableOptions: {
      handler(newOptions) {
        const payload = {
          page: newOptions.page,
          size: newOptions.itemsPerPage
        };
        if (this.dates.length === 2) {
          const sortedDates = [...this.dates].sort();
          const [startDate, endDate] = sortedDates;
          payload.startDate = startDate;
          payload.endDate = endDate;
        }
        this.$store.dispatch("reports/getTransactionStatisticsByDate", payload);
      },
      deep: true
    }
  },
  methods: {
    money(value) {
      return `GHS ${Number(value || 0).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },
    compactAmount(value) {
      const n = Number(value || 0);
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
      return n.toFixed(0);
    },
    tooltipDate(opts) {
      const idx =
        opts && typeof opts.dataPointIndex === "number"
          ? opts.dataPointIndex
          : -1;
      const d = this.chartSeries[idx];
      return d ? moment(d).format("MMMM DD, YYYY") : "";
    },
    bucketLabel(id) {
      const map = {
        0: "< 10",
        10: "10 – 50",
        50: "50 – 100",
        100: "100 – 500",
        500: "500 – 1K",
        1000: "1K – 5K",
        "5000+": "5K+"
      };
      return map[id] != null ? map[id] : String(id);
    },
    fetchAnalytics() {
      const payload = {};
      if (this.dates.length === 2) {
        const [startDate, endDate] = [...this.dates].sort();
        payload.startDate = startDate;
        payload.endDate = endDate;
      }
      this.$store.dispatch("reports/getTransactionAnalytics", payload);
    },
    setFilteredDate() {
      this.$refs.menu.save(this.dates);
      if (this.dates.length === 2) {
        const sortedDates = [...this.dates].sort(); // Create a copy before sorting
        const [startDate, endDate] = sortedDates;
        this.$store.dispatch("reports/getTransactionStatisticsByDate", {
          startDate,
          endDate,
          page: this.tableOptions.page,
          size: this.tableOptions.itemsPerPage
        });
      }
      this.fetchAnalytics();
    },
    clearDateFilter() {
      this.dates = [];
      this.menu = false;
      this.$store.dispatch("reports/getTransactionStatisticsByDate", {
        page: this.tableOptions.page,
        size: this.tableOptions.itemsPerPage
      });
      this.fetchAnalytics();
    },
    refreshData() {
      if (this.dates.length === 2) {
        const sortedDates = [...this.dates].sort(); // Create a copy before sorting
        const [startDate, endDate] = sortedDates;
        this.$store.dispatch("reports/getTransactionStatisticsByDate", {
          startDate,
          endDate,
          page: this.tableOptions.page,
          size: this.tableOptions.itemsPerPage
        });
      } else {
        this.$store.dispatch("reports/getTransactionStatisticsByDate", {
          page: this.tableOptions.page,
          size: this.tableOptions.itemsPerPage
        });
      }
      this.fetchAnalytics();
    }
  },
  created() {
    this.$store.dispatch("reports/getTransactionStatisticsByDate", {
      page: this.tableOptions.page,
      size: this.tableOptions.itemsPerPage
    });
    this.fetchAnalytics();
  }
};
</script>

<style scoped>
.toolbar-btn {
  border-color: var(--border) !important;
  color: var(--muted-foreground) !important;
  border-radius: var(--radius) !important;
  height: 40px !important;
  font-weight: 500;
}
.toolbar-btn--active {
  border-color: var(--signal) !important;
  color: var(--signal) !important;
}
.toolbar__refresh {
  color: var(--muted-foreground);
}

.stat-tile {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-card);
  padding: 18px 20px;
  height: 100%;
}
.stat-tile__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-tile__icon--brand {
  background: var(--signal-soft);
}
.stat-tile__icon--brand .v-icon {
  color: var(--signal);
}
.stat-tile__icon--success {
  background: var(--status-delivered-soft);
}
.stat-tile__icon--success .v-icon {
  color: var(--status-delivered);
}
.stat-tile__icon--info {
  background: var(--status-sent-soft);
}
.stat-tile__icon--info .v-icon {
  color: var(--status-sent);
}
.stat-tile__label {
  margin: 0 0 3px;
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.stat-tile__value {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 22px;
  font-weight: 600;
  color: var(--foreground);
}
.stat-tile__value--sm {
  font-family: var(--font-sans);
  font-size: 15px;
}

.panel {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  height: 100%;
}
.panel__head {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 15px;
  color: var(--foreground);
}
.panel__body {
  padding: 12px 12px 4px;
}
.panel__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
}
.cell-strong {
  font-weight: 600;
  color: var(--foreground);
}
.cell-amount {
  color: var(--status-delivered);
  font-weight: 600;
}
.text-muted {
  color: var(--muted-foreground);
}
</style>
